import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { StorageCluster as Cluster, StorageCluster_ItemPerStorageTier } from '@schema';
import {
  CLUSTER_STATUSES_TYPES_MAP,
  STORAGE_CLUSTER_STATUSES,
  STORAGE_CLUSTER_STATUSES_TEXT
} from '@/common/constants/clusters.constants';
import api from '@/common/utils/api';
import CommonError from '@/common/utils/errors/CommonError';
import ClusterNotFoundError from '@/common/utils/errors/ClusterNotFoundError';
import ability from '@/common/ability';
import takeFirst from '@/common/utils/takeFirst';

const mapCluster = (cluster: Cluster) => ({
  id: cluster.id.toString() as ID,
  name: cluster.name,
  status: cluster.status as STORAGE_CLUSTER_STATUSES,
  statusText: (STORAGE_CLUSTER_STATUSES_TEXT[cluster.status]
    || STORAGE_CLUSTER_STATUSES_TEXT[STORAGE_CLUSTER_STATUSES.ERROR]) as typeof STORAGE_CLUSTER_STATUSES_TEXT,
  statusType: (CLUSTER_STATUSES_TYPES_MAP[cluster.status]
    || CLUSTER_STATUSES_TYPES_MAP.error) as typeof CLUSTER_STATUSES_TYPES_MAP,
  tiers: cluster.tiers,
  activeDisks: cluster.active_cses,
  activeNodes: cluster.active_nodes,
  activeRacks: cluster.active_racks,
  activeRows: cluster.active_rows,
  activeRooms: cluster.active_rooms,
  licenseCapacity: cluster.license?.capacity || -1,
  chunks: cluster.chunks,
  repl: cluster.repl,
  logicSpace: cluster.logic_space,
  spacePerService: cluster.space_per_service,
  activeMDS: (cluster.mds?.total || 0) - (cluster.mds?.failed || 0),
  activeCS: (cluster.cs?.total || 0) - (cluster.cs?.failed || 0),
});

export type StorageCluster = ReturnType<typeof mapCluster>;

export const useStorageClusterStore = defineStore('storageCluster', () => {
  const clusters = ref([]);

  // getters
  const current = computed<StorageCluster | null>(() => clusters.value[0] ? mapCluster(clusters.value[0]) : null);
  const status = computed(() => current.value?.status);
  const statusText = computed(() => current.value?.statusText);
  const statusType = computed(() => current.value?.statusType);
  const activeMDS = computed(() => current.value?.activeMDS || 0);
  const activeCS = computed(() => Object.values(current.value?.activeDisks || {}).reduce((res, cur) => res + cur, 0));

  const getPerTier = (key: 'activeNodes' | 'activeDisks' | 'activeRacks' | 'activeRows' | 'activeRooms') => computed(
    () => (tier: keyof StorageCluster_ItemPerStorageTier): number => current.value?.[key]?.[tier] !== undefined
      ? current.value[key]?.[tier] || 0
      : 0
  );
  const hostsPerTier = getPerTier('activeNodes');
  const disksPerTier = getPerTier('activeDisks');
  const racksPerTier = getPerTier('activeRacks');
  const rowsPerTier = getPerTier('activeRows');
  const roomsPerTier = getPerTier('activeRooms');

  // actions

  const createCluster = ({ model, disks }) => api.post('/api/v2/clusters', {
    ...model,
    disks
  });

  const joinNode = ({ nodeId, disks }: { nodeId: ID, disks: object[] }) => {
    if (!current.value) {
      throw new CommonError('No cluster available');
    }
    return api.post(`/api/v2/${current.value.id}/nodes`, {
      node_id: nodeId,
      disks
    });
  };

  const releaseNode = ({ nodeId, force }: { nodeId: ID, force: boolean }) => api.post(`/api/v2/nodes/${nodeId}/release`, { force });

  const fetch = (payload?: { options?: object, [key: string]: any }) => {
    if (!ability.can('read', 'clusters_overview')) {
      return Promise.resolve();
    }
    const { options, ...params } = payload || {};
    return api.get('/api/v2/overview/', params, options)
      .then((response) => {
        if (response.data?.data && Array.isArray(response.data.data)) {
          clusters.value = response.data.data;
          return response.data.data;
        }
        throw new ClusterNotFoundError();
      });
  };

  const getClusterId = async (): Promise<ID> => {
    if (current.value?.id) {
      return current.value.id;
    }

    const items = await takeFirst(fetch);
    const cluster = items?.[0];
    if (cluster?.id) {
      return cluster.id;
    }
    throw new ClusterNotFoundError();
  };

  const flushState = () => {
    clusters.value = [];
  };

  // WS
  const onNodeJoined = () => fetch();
  const onNodeReleased = () => fetch();
  const onServices = (data) => {
    const kind = data.type || data.kind;
    const operation = data.event || data.operation;

    if (kind === 'cluster' && operation === 'change') {
      if (!current.value) {
        fetch();
        return;
      }

      Object.assign(clusters.value[0], {
        id: data.cluster_id,
        status: data.curr_status
      });
    }
  };
  return {
    clusters,
    // getters
    current,
    status,
    statusText,
    statusType,
    activeMDS,
    activeCS,
    hostsPerTier,
    disksPerTier,
    racksPerTier,
    rowsPerTier,
    roomsPerTier,

    // actions
    fetch,
    getClusterId,
    createCluster,
    joinNode,
    releaseNode,
    flushState,

    // ws
    onNodeJoined,
    onNodeReleased,
    onServices
  };
}, {
  ws: {
    services: 'onServices',
    node_joined: 'onNodeJoined',
    node_released: 'onNodeReleased'
  }
});

export default useStorageClusterStore;
